import { Edit, Trash2, UserIcon } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { buildApiUrl } from "../../lib/utils";
import { useGetUserByIdQuery } from "./userApiSlice";
import UserModal from "./UserModal";
import DeleteUserModal from "./ui/DeleteUserModal";

// Skeleton Components
const ProfileHeaderSkeleton = () => (
  <div className="flex items-center justify-between p-8 bg-gray-50 border-b">
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 bg-gray-300 rounded-full animate-pulse" />
      <div className="space-y-2">
        <div className="h-6 w-32 bg-gray-300 rounded animate-pulse" />
        <div className="h-4 w-48 bg-gray-300 rounded animate-pulse" />
      </div>
    </div>
    <div className="h-10 w-24 bg-gray-300 rounded animate-pulse" />
  </div>
);

const ProfileSectionSkeleton = () => (
  <div className="p-8">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
      <div className="h-6 w-32 bg-gray-300 rounded animate-pulse" />
      <div className="h-10 w-28 bg-gray-300 rounded animate-pulse" />
    </div>

    <div className="space-y-8">
      {[...Array(5)].map((_, index) => (
        <div key={index} className="flex">
          <div className="w-40">
            <div className="h-4 w-20 bg-gray-300 rounded animate-pulse" />
          </div>
          <div className="flex-1">
            <div className="h-4 w-48 bg-gray-300 rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DashUserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isLoading, isError, refetch } = useGetUserByIdQuery(id || "", {
    skip: !id,
  });

  const [isUpdateUserModalOpen, setIsUpdateUserModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <ProfileHeaderSkeleton />
        <ProfileSectionSkeleton />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-hColor mb-2">
            User not found
          </h2>
          <p className="text-pColor mb-4">
            The user you're looking for doesn't exist.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  const userData = data.data;

  return (
    <div className="min-h-screen p-4 sm:p-8">
      {/* Header Section */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-white rounded-[10px]">
        <div className="flex items-center gap-4">
          <div className="w-[55px] h-[55px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
            {userData.imageUrl && userData.imageUrl !== "" ? (
              <img
                src={buildApiUrl(userData.imageUrl)}
                alt={userData.fullName}
                className="w-20 h-20 object-cover rounded-full"
              />
            ) : (
              <UserIcon className="w-full h-full text-hColor" />
            )}
          </div>
          <div>
            <h1 className="text-md font-semibold text-hColor">
              {userData.fullName}
            </h1>
            <p className="text-pColor">{userData.email}</p>
          </div>
        </div>

        <button
          onClick={() => setIsDeleteUserModalOpen(true)}
          className="text-[13px] font-semibold flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-[5px] hover:bg-red-100 transition-colors"
        >
          <Trash2 size={16} />
          Delete
        </button>
      </div>

      {/* Profile Details Section */}
      <div className="bg-white rounded-[10px] mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 sm:p-6">
          <h2 className="text-md font-semibold text-hColor">
            {userData.userType} profile
          </h2>
          <button
            onClick={() => setIsUpdateUserModalOpen(true)}
            className="text-[13px] font-semibold flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-[5px] hover:bg-primary/85 transition-colors"
          >
            Edit profile
            <Edit size={13} />
          </button>
        </div>

        <div className="border-b border-gray-300 w-full b-[0.5px]"></div>

        {/* Details Grid */}
        <div className="space-y-6 sm:space-y-10 p-4 sm:p-6 text-[15px]">
          <div className="flex">
            <div className="w-[35vw] sm:w-[20vw]">
              <span className="text-pColor">Name</span>
            </div>
            <div className="flex-1">
              <span className="text-hColor font-semibold">
                {userData.fullName}
              </span>
            </div>
          </div>

          <div className="flex">
            <div className="w-[35vw] sm:w-[20vw]">
              <span className="text-pColor">Role</span>
            </div>
            <div className="flex-1">
              <span className="text-hColor font-semibold">
                {userData.roles}
              </span>
            </div>
          </div>

          <div className="flex">
            <div className="w-[35vw] sm:w-[20vw]">
              <span className="text-pColor">Email address</span>
            </div>
            <div className="flex-1">
              <span className="text-hColor font-semibold">
                {userData.email}
              </span>
            </div>
          </div>

          <div className="flex">
            <div className="w-[35vw] sm:w-[20vw]">
              <span className="text-pColor">Phone number</span>
            </div>
            <div className="flex-1">
              <span className="text-hColor font-semibold">
                {userData.phoneNumber}
              </span>
            </div>
          </div>

          <div className="flex">
            <div className="w-[35vw] sm:w-[20vw]">
              <span className="text-pColor">Home address</span>
            </div>
            <div className="flex-1">
              <span className="text-hColor font-semibold">
                {userData.address}
              </span>
            </div>
          </div>
        </div>
      </div>
      {isUpdateUserModalOpen && userData && (
        <UserModal
          isOpen={isUpdateUserModalOpen}
          onClose={() => {
            setIsUpdateUserModalOpen(false);
            refetch();
          }}
          user={userData}
          mode="edit"
        />
      )}
      <DeleteUserModal
        isOpen={isDeleteUserModalOpen}
        onClose={() => setIsDeleteUserModalOpen(false)}
        userId={userData.id}
        entityLabel="user"
        redirectTo={
          location.pathname.startsWith("/admin")
            ? "/admin/users"
            : "/super-admin/users"
        }
      />
    </div>
  );
};

export default DashUserDetails;
